import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'equasion-lib';
import { FiAlertTriangle, FiMaximize } from 'react-icons/fi';
import {
  combineAlertCopy,
  combineAlertOrders,
  combineStageText,
  type CombineContext,
  type CombineVariant
} from '@/lib/combineShipment';
import './CombineShipment.scss';

const click = (onClick: () => void) => ({ onClick });

/**
 * THE ALERT, the moment it first applies: at the end of the pick path (before Complete Pick), or as
 * a Verify screen opens. A bottom sheet over the screen, with the alert tone, so it is not missed on
 * the floor; once dismissed the same message stays on the screen as the combine card.
 *
 * Primary: scan the other order's pick ticket now. Secondary: carry on (Pick: the tote is set aside
 * for the other order; Verify: combine it when it arrives).
 */
function CombineAlertSheet({
  variant,
  context,
  onScan,
  onDismiss
}: {
  variant: CombineVariant;
  context: CombineContext | null;
  onScan: () => void;
  onDismiss: () => void;
}) {
  const orders = combineAlertOrders(context);
  if (!orders.length) return null;
  const copy = combineAlertCopy(variant, context);
  return createPortal(
    <div className="combine-sheet-backdrop" role="dialog" aria-modal="true" aria-label={copy.title}>
      <div className="combine-sheet">
        <div className="combine-sheet__icon" aria-hidden="true">
          <FiAlertTriangle />
        </div>
        <p className="combine-sheet__title">{copy.title}</p>
        <p className="combine-sheet__text">{copy.text}</p>
        <ul className="combine-sheet__orders">
          {orders.map((order) => (
            <li key={order.id}>
              <span className="combine-sheet__so">SO #{order.vendorSalesOrderNumber}</span>
              <span className="combine-sheet__stage">{combineStageText(order)}</span>
            </li>
          ))}
        </ul>
        <div className="combine-sheet__actions">
          <Button type="primary" fullWidth iconBefore={<FiMaximize />} {...click(onScan)}>
            Scan Pick Ticket to Combine
          </Button>
          <Button type="secondary" fullWidth {...click(onDismiss)}>
            {variant === 'pick' ? 'OK — I’ll set it aside' : 'Not now'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CombineAlertSheet;
